import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { APP_NAME } from '../../constants/app-branding.constants';
import { StateStoreService } from '../../services/state-store/state-store';
import { ShellChromeService } from '../../services/shell-chrome/shell-chrome.service';
import { IconComponent } from '../icon/icon';
import { ActionGlyphComponent } from '../action-glyph/action-glyph';
import { SidebarPlanWidgetComponent } from '../sidebar-plan-widget/sidebar-plan-widget';
import { SidebarHeader } from './sidebar-header/sidebar-header';
import { SidebarNavGroup } from './sidebar-nav-group/sidebar-nav-group';
import { openRouteInNewTab } from '../../utils/navigation.utils';

/**
 * App shell sidenav.
 *
 * Open → full rail (brand + nav + org footer). Closed → width 0 (fully
 * retracted). The open/close toggle is owned by the app shell on the
 * far left of Veritise so it stays fixed while this host animates.
 * Open/closed state lives on {@link ShellChromeService}.
 */
@Component({
  selector: 'app-sidebar',
  imports: [
    RouterLink,
    RouterLinkActive,
    IconComponent,
    ActionGlyphComponent,
    MatTooltipModule,
    SidebarPlanWidgetComponent,
    SidebarHeader,
    SidebarNavGroup,
    TranslocoModule,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sidebar-host',
    '[class.is-closed]': '!sidebarOpen()',
    // Drop from a11y tree when fully retracted — width:0 alone still
    // leaves links/buttons in the tab order.
    '[attr.inert]': '!sidebarOpen() ? "" : null',
    '[attr.aria-hidden]': '!sidebarOpen()',
  },
})
export class Sidebar {
  private readonly stateService = inject(StateStoreService);
  private readonly shellChrome = inject(ShellChromeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);

  protected readonly appName = APP_NAME;
  readonly sidebarOpen = this.shellChrome.sidebarOpen;

  /** Import Agent — brand-row icon for MANAGER / SUPER_MANAGER /
   *  SELF_MANAGED compliance operators (opens `/import` in a new tab). */
  readonly canAccessImportAgent = this.stateService.canManageCompliance;

  /**
   * Import Agent href, computed to include the current sticky params
   * (`orgId` / `view`).
   *
   * Why not just `href="/import"`? Middle-click and right-click "Open
   * in new tab" bypass the (click) handler entirely and navigate to
   * the raw href. Without sticky params in the URL, the new tab boots
   * with the user's OWN org (managers land on their manager org, not
   * on the customer they were working with). `loadResumableSessions`
   * then calls `/imports` scoped to the wrong org; the BE returns
   * `hint=SWITCH_ORG`, and `AccessErrorDispatcher` bounces the user
   * to `/dashboard` — the exact "clicks import, loads, bounces to
   * dashboard" symptom.
   *
   * Baking the sticky params into the href keeps ALL three interaction
   * paths (left-click, middle-click, right-click → open in new tab)
   * carrying the same scope. The (click) handler forces a new tab
   * even on left-click.
   */
  readonly importAgentHref = computed(() => {
    const orgId = this.stateService.managedCustomerOrgId();
    const view = this.viewMode();
    const parts: string[] = [];
    if (orgId) parts.push(`orgId=${encodeURIComponent(orgId)}`);
    if (view === 'manager') parts.push('view=manager');
    return parts.length ? `/import?${parts.join('&')}` : '/import';
  });

  get isStateReady() {
    return this.stateService.isStateReady();
  }

  get isAdmin() {
    return this.stateService.isAdmin();
  }

  readonly canAccessTrustCenter = this.stateService.canAccessTrustCenter;
  readonly canAccessFrameworks = computed(() => this.stateService.hasCapability('framework_read'));
  /** Vendors cluster — role/relationship only; destination pages own upsell. */
  readonly canAccessVendors = this.stateService.canAccessVendors;
  readonly canReviewAssessments = this.stateService.canReviewAssessments;
  readonly canManageVendorRegistry = this.stateService.canManageVendorRegistry;
  readonly viewMode = this.stateService.viewMode;
  readonly isManagerView = computed(() => this.viewMode() === 'manager');

  hasActiveSubscription = this.stateService.hasActiveSubscription;
  subscriptionLoading = this.stateService.subscriptionLoading;
  isBillingSetupPending = this.stateService.isBillingSetupPending;
  isPaymentOverdue = this.stateService.isPaymentOverdue;

  readonly showSubscriptionWidget = computed(() => {
    return (
      !this.subscriptionLoading() && (!this.hasActiveSubscription() || this.isPaymentOverdue())
    );
  });

  private readonly translationVersion = signal(0);

  readonly subscriptionWidgetMessage = computed(() => {
    this.translationVersion();
    if (this.isPaymentOverdue())
      return this.transloco.translate('sidebar.subscription_payment_overdue');
    if (this.isBillingSetupPending())
      return this.transloco.translate('sidebar.subscription_setup_pending');
    return this.transloco.translate('sidebar.subscription_no_active');
  });

  readonly subscriptionWidgetActionText = computed(() => {
    this.translationVersion();
    if (this.isPaymentOverdue())
      return this.transloco.translate('sidebar.subscription_action_update_payment');
    if (this.isBillingSetupPending())
      return this.transloco.translate('sidebar.subscription_action_add_address');
    return this.transloco.translate('sidebar.subscription_action_upgrade');
  });

  /** Stable path lists for `<app-sidebar-nav-group activePaths>`. */
  protected readonly compliancePaths = [
    '/assessment',
    '/controls',
    '/frameworks',
    '/documents',
  ] as const;
  protected readonly vendorsPaths = ['/vendors'] as const;
  protected readonly trustCenterPaths = ['/trust-center'] as const;
  protected readonly forReviewPaths = ['/assessments-for-review'] as const;
  protected readonly customersPaths = ['/customers'] as const;

  constructor() {
    this.transloco.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.translationVersion.update(v => v + 1);
    });
  }

  navigateToBilling(): void {
    this.router.navigate(['/billing'], { queryParams: { tab: 'subscription' } });
  }

  /** Import Agent opens in a new tab — full-screen assistant. Sticky
   *  `orgId` / `view` query params travel with the link. */
  openImportAgent(event: Event): void {
    event.preventDefault();
    openRouteInNewTab(this.router, ['/import']);
  }
}
